import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid, makeStyles } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { OverviewChartLoading } from '~/modules/common/charts/dashboard';
import { PERIOD_SCALE_ENUM } from '~/modules/common/charts/timeline/periodScale';
import { formatDateRangeLabel } from '~/modules/common/dates/format';
import { useProjectKPISummary } from './hooks';
import ProjectKPIItem from './ProjectKPIItem';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex'
  }
}));

const safeRatio = (n, d) => (d ? n / d : null);

const getTCINaTooltipMessage = ({
  formatMessage,
  isEntireProject,
  dateRange,
  scale
}) => {
  return formatMessage(
    {
      id: isEntireProject
        ? 'projectKPI.timesheetComplianceIndex.naTooltip.entireProject'
        : 'projectKPI.timesheetComplianceIndex.naTooltip.period'
    },
    {
      period: isEntireProject
        ? ''
        : formatDateRangeLabel(
            scale,
            dateRange.startDate,
            dateRange.endDate,
            formatMessage
          )
    }
  );
};

const getBINaTooltipMessage = ({
  formatMessage,
  isEntireProject,
  dateRange,
  scale
}) => {
  return formatMessage(
    {
      id: isEntireProject
        ? 'projectKPI.billabilityIndex.naTooltip.entireProject'
        : 'projectKPI.billabilityIndex.naTooltip.period'
    },
    {
      period: isEntireProject
        ? ''
        : formatDateRangeLabel(
            scale,
            dateRange.startDate,
            dateRange.endDate,
            formatMessage
          )
    }
  );
};

const getSPINaTooltipMessage = ({
  formatMessage,
  isEntireProject,
  dateRange,
  tasksCount,
  scale
}) => {
  if (tasksCount === 0)
    return formatMessage({
      id: 'projectKPI.schedulePerformanceIndex.naTooltip.noTasks'
    });

  return formatMessage(
    {
      id: isEntireProject
        ? 'projectKPI.schedulePerformanceIndex.naTooltip.noPlannedEndDatesEntireProject'
        : 'projectKPI.schedulePerformanceIndex.naTooltip.noPlannedEndDatesPeriod'
    },
    {
      period: isEntireProject
        ? ''
        : formatDateRangeLabel(
            scale,
            dateRange.startDate,
            dateRange.endDate,
            formatMessage
          )
    }
  );
};

export const ProjectKPIChart = ({ chartDataInput }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const {
    projectId,
    dateRange,
    scale,
    projectPermissions: { canViewTasks, canViewProjectBillingData },
    isExpenseProductEnabled
  } = chartDataInput;
  const isEntireProject = scale === PERIOD_SCALE_ENUM.ENTIRE_PROJECT;

  const {
    teamMembersTimesheetCountSummary,
    billableNonBillableHoursSummary,
    tasksCompletionCountSummary,
    tasksCount,
    loading
  } = useProjectKPISummary({
    projectId,
    dateRange
  });

  const canViewSPIIndex = canViewTasks;
  const canViewBIIndex = canViewProjectBillingData && isExpenseProductEnabled;

  const visibleKpiCount = [true, canViewBIIndex, canViewSPIIndex].filter(
    Boolean
  ).length;
  const gridSize = 12 / visibleKpiCount;

  const kpiPercentages = useMemo(
    () => ({
      tci: safeRatio(
        (teamMembersTimesheetCountSummary?.waitingTimesheetCount ?? 0) +
          (teamMembersTimesheetCountSummary?.approvedTimesheetCount ?? 0),
        teamMembersTimesheetCountSummary?.totalTimesheetCount
      ),
      bi: safeRatio(
        billableNonBillableHoursSummary?.billableHours,
        billableNonBillableHoursSummary?.totalHours
      ),
      spi: safeRatio(
        tasksCompletionCountSummary?.closedOnOrBeforeTaskEndDateCount,
        tasksCompletionCountSummary?.totalTaskCount
      )
    }),
    [
      teamMembersTimesheetCountSummary,
      billableNonBillableHoursSummary,
      tasksCompletionCountSummary
    ]
  );

  if (loading) return <OverviewChartLoading />;

  return (
    <Grid container className={classes.container} spacing={2}>
      <Grid item lg={gridSize} md={gridSize} xs={6}>
        <ProjectKPIItem
          acronym={formatMessage({
            id: 'projectKPI.timesheetComplianceIndex.acronym'
          })}
          label={formatMessage({
            id: 'projectKPI.timesheetComplianceIndex.label'
          })}
          naTooltipMessage={getTCINaTooltipMessage({
            formatMessage,
            isEntireProject,
            dateRange,
            scale
          })}
          value={kpiPercentages.tci}
          scale={scale}
          dateRange={dateRange}
          dataQeId="TimesheetComplianceIndexKPI"
        />
      </Grid>
      {canViewBIIndex && (
        <Grid item lg={gridSize} md={gridSize} xs={6}>
          <ProjectKPIItem
            acronym={formatMessage({
              id: 'projectKPI.billabilityIndex.acronym'
            })}
            label={formatMessage({
              id: 'projectKPI.billabilityIndex.label'
            })}
            naTooltipMessage={getBINaTooltipMessage({
              formatMessage,
              isEntireProject,
              dateRange,
              scale
            })}
            value={kpiPercentages.bi}
            scale={scale}
            dateRange={dateRange}
            dataQeId="BillabilityIndexKPI"
          />
        </Grid>
      )}
      {canViewSPIIndex && (
        <Grid item lg={gridSize} md={gridSize} xs={6}>
          <ProjectKPIItem
            acronym={formatMessage({
              id: 'projectKPI.schedulePerformanceIndex.acronym'
            })}
            label={formatMessage({
              id: 'projectKPI.schedulePerformanceIndex.label'
            })}
            naTooltipMessage={getSPINaTooltipMessage({
              formatMessage,
              isEntireProject,
              dateRange,
              tasksCount,
              scale
            })}
            value={kpiPercentages.spi}
            scale={scale}
            dateRange={dateRange}
            dataQeId="SchedulePerformanceIndexKPI"
          />
        </Grid>
      )}
    </Grid>
  );
};

ProjectKPIChart.propTypes = {
  chartDataInput: PropTypes.shape({
    projectId: PropTypes.string.isRequired,
    dateRange: PropTypes.object,
    scale: PropTypes.string,
    projectPermissions: PropTypes.object,
    isExpenseProductEnabled: PropTypes.bool
  }).isRequired
};

export default ProjectKPIChart;
